import React from 'react';
import { useTranslation } from 'react-i18next';
import { textToDate } from 'utils/common';
import { Box, Typography } from '@p81-common/ui';

interface Sku {
  sku: string;
  expiresAt: string;
  quantity: number | null;
}

interface SaseSkusListProps {
  skus: Sku[] | null;
  planId: string | null;
  error?: boolean;
}

// SASE SKUs only exist for CP (Check Point) plans. Show the section for any
// cp-* plan — with a "No SKUs found" empty state when there are none — and hide
// it entirely for non-CP plans (e.g. P81-only), which never have SKUs.
const isCpPlan = (planId: string | null) =>
  (planId || '').toLowerCase().startsWith('cp-');

const SaseSkusList = ({ skus, planId, error = false }: SaseSkusListProps) => {
  const { t } = useTranslation('activity');

  if (!isCpPlan(planId)) {
    return null;
  }

  const skuList = skus || [];

  const heading = (
    <Typography
      variant="h4"
      mb={1.5}
      sx={{ color: '#394463', fontSize: '20px' }}
    >
      {t('activity:customers:SASE_SKUS_HEADING')}
    </Typography>
  );

  if (error) {
    return (
      <Box data-testid="sase-skus-section">
        {heading}
        <Typography variant="body2" color="error" data-testid="skus-error">
          {t('activity:customers:SASE_SKUS_ERROR')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box data-testid="sase-skus-section">
      {heading}
      {skuList.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('activity:customers:SASE_SKUS_EMPTY_STATE')}
        </Typography>
      ) : (
        <Box component="ul" sx={{ listStyle: 'none', m: 0, py: 0, pl: 1.5 }}>
          {skuList.map(item => (
            <Box
              key={item.sku}
              component="li"
              sx={{
                minHeight: 48,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                borderBottom: '1px solid',
                borderColor: 'divider',
                '&:last-child': { borderBottom: 'none' }
              }}
            >
              <Typography variant="body2" sx={{ color: '#394463' }}>
                {item.sku}
                {item.quantity !== null && (
                  <Box component="span" sx={{ color: '#3e88ff', ml: 0.5 }}>
                    ({item.quantity})
                  </Box>
                )}
              </Typography>
              <Typography variant="caption" display="block">
                <Box component="span" sx={{ color: '#42526D' }}>
                  {t('activity:customers:SASE_SKUS_EXPIRATION_DATE')}
                </Box>{' '}
                <Box component="span" sx={{ color: '#6B788E' }}>
                  {textToDate(item.expiresAt)}
                </Box>
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default SaseSkusList;
